import React, { useState, useEffect } from 'react';
import { X, Filter } from 'lucide-react';

interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: any) => void;
  onReset: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({ isOpen, onClose, onApplyFilters, onReset }) => {
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: '',
    noOfPax: '',
    paxOperator: 'eq',
    paxMin: '',
    paxMax: '',
    noOfChildren: '',
    childrenOperator: 'eq',
    childrenMin: '',
    childrenMax: '',
    agencyName: '',
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleReset = () => {
    setFilters({ 
      status: '', 
      dateFrom: '', 
      dateTo: '', 
      noOfPax: '', 
      paxOperator: 'eq',
      paxMin: '',
      paxMax: '',
      noOfChildren: '', 
      childrenOperator: 'eq',
      childrenMin: '',
      childrenMax: '',
      agencyName: '' 
    });
    onReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" onClick={onClose}></div>
      
      <div className="fixed right-0 top-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 animate-slide-up">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Filters</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="PENDING_CONFIRMATION">Pending Confirmation</option>
                <option value="TENTATIVE">Tentative</option>
                <option value="CONVERTED">Converted</option>
                <option value="PENDING_CANCELLATION">Pending Cancellation</option>
                <option value="CANCELED">Canceled</option>
              </select>
            </div>



            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => {
                  const newFilters = { ...filters, dateFrom: e.target.value };
                  if (e.target.value && !filters.dateTo) {
                    newFilters.dateTo = e.target.value;
                  }
                  setFilters(newFilters);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Agency Name</label>
              <input
                type="text"
                value={filters.agencyName}
                onChange={(e) => setFilters({ ...filters, agencyName: e.target.value })}
                placeholder="Enter agency name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">No of Adults</label>
              <div className="space-y-2">
                <select
                  value={filters.paxOperator}
                  onChange={(e) => setFilters({ ...filters, paxOperator: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="eq">Equals (=)</option>
                  <option value="gt">Greater than (&gt;)</option>
                  <option value="lt">Less than (&lt;)</option>
                  <option value="between">Between</option>
                </select>
                
                {filters.paxOperator === 'between' ? (
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      min="0"
                      value={filters.paxMin}
                      onChange={(e) => setFilters({ ...filters, paxMin: e.target.value })}
                      placeholder="Min"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      min="0"
                      value={filters.paxMax}
                      onChange={(e) => setFilters({ ...filters, paxMax: e.target.value })}
                      placeholder="Max"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ) : (
                  <input
                    type="number"
                    min="0"
                    value={filters.noOfPax}
                    onChange={(e) => setFilters({ ...filters, noOfPax: e.target.value })}
                    placeholder="Enter number of adults"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">No of Children</label>
              <div className="space-y-2">
                <select
                  value={filters.childrenOperator}
                  onChange={(e) => setFilters({ ...filters, childrenOperator: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="eq">Equals (=)</option>
                  <option value="gt">Greater than (&gt;)</option>
                  <option value="lt">Less than (&lt;)</option>
                  <option value="between">Between</option>
                </select>
                
                {filters.childrenOperator === 'between' ? (
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      min="0"
                      value={filters.childrenMin}
                      onChange={(e) => setFilters({ ...filters, childrenMin: e.target.value })}
                      placeholder="Min"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      min="0"
                      value={filters.childrenMax}
                      onChange={(e) => setFilters({ ...filters, childrenMax: e.target.value })}
                      placeholder="Max"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ) : (
                  <input
                    type="number"
                    min="0"
                    value={filters.noOfChildren}
                    onChange={(e) => setFilters({ ...filters, noOfChildren: e.target.value })}
                    placeholder="Enter number of children"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 space-y-3">
            <button
              onClick={handleApply}
              className="w-full px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-medium"
            >
              Apply Filters
            </button>
            <button
              onClick={handleReset}
              className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-medium"
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterPanel;
